import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Download, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { ScreenShell, GlassCard, GradientText, SimHeader } from "@/components/sim/shared";
import { apiCall, ApiError } from "@/lib/api";

interface ExportResponse {
  _meta?: { exportedAt?: string; subject?: { email?: string; fullName?: string } };
  [k: string]: unknown;
}

/**
 * /my/data — F-007 self-serve DSAR download.
 *
 * Any authenticated user (learner / client_admin / super_admin) can hit
 * this page and download their own personal-data bundle as JSON. Rate-
 * limited server-side to once per 24 hours.
 */
export function MyDataPage(): JSX.Element {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ at: string; subject: string } | null>(null);

  const download = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiCall<ExportResponse>("/sim-myData", {
        method: "POST",
        timeoutMs: 60_000,
      });
      // Trigger download as data-{email}-{date}.json
      const subject = res._meta?.subject?.email ?? "user";
      const safeName = String(subject).replace(/[^a-z0-9]+/gi, "_").toLowerCase();
      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([JSON.stringify(res, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `neusim-data-${safeName}-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDone({
        at: res._meta?.exportedAt ?? new Date().toISOString(),
        subject: String(res._meta?.subject?.email ?? "your account"),
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Download failed");
    } finally { setBusy(false); }
  };

  return (
    <ScreenShell>
      <SimHeader decision={0} xp={0} />
      <main className="mx-auto max-w-2xl px-4 pb-20 md:px-8">
        <button type="button" onClick={() => navigate(-1)}
                className="mb-5 inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-white">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-2 text-emerald-300">
            <ShieldCheck className="h-4 w-4" />
            <span className="text-[10px] font-bold uppercase tracking-[0.18em]">My data</span>
          </div>
          <h1 className="mt-2 text-[28px] font-bold leading-tight">
            <GradientText>Download a copy of everything we hold on you</GradientText>
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-white/65">
            Under data-protection law you have the right to a copy of all the personal data NeuSim
            holds on you. Click the button below and we'll generate a structured JSON file with the
            full record. You can take it elsewhere or keep it for your own reference.
          </p>
        </motion.div>

        <GlassCard className="mt-6 p-5">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">What's in the file</h2>
          <ul className="mt-2 space-y-1.5 text-[13px] leading-relaxed text-white/75">
            <li>• Your profile (name, email, sign-up + last-login)</li>
            <li>• Every cohort you've ever been part of, with project / client context</li>
            <li>• Every simulation attempt — your scores, the path you took, your reflection, your AI coaching</li>
            <li>• Every answer you typed, with the AI's feedback and scoring</li>
            <li>• Behavioural signals we collected (paste / tab-blur / typing-rhythm counts) — disclosed up front</li>
            <li>• A record of every admin action ever taken on your account</li>
          </ul>
          <h2 className="mt-5 text-[11px] font-bold uppercase tracking-[0.14em] text-white/45">What's not included</h2>
          <ul className="mt-2 space-y-1.5 text-[13px] leading-relaxed text-white/55">
            <li>· Other learners' data</li>
            <li>· Cohort-level AI insights (those are aggregated, not about you)</li>
            <li>· Internal simulation content (NeuSim's own IP)</li>
          </ul>
        </GlassCard>

        {error !== null && (
          <div className="mt-4 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-[13px] text-red-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="font-semibold">{error}</div>
            </div>
          </div>
        )}

        {done !== null && (
          <div className="mt-4 rounded-xl border border-emerald-400/40 bg-emerald-500/10 px-4 py-3 text-[13px] text-emerald-100">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-semibold">Your data has been downloaded.</div>
                <div className="mt-1 text-[11.5px] text-emerald-100/80">
                  File for {done.subject} · {new Date(done.at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-between gap-3">
          <p className="text-[11px] text-white/45">
            You can request a fresh download once every 24 hours.
          </p>
          <button type="button" disabled={busy} onClick={() => void download()}
                  className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-500/20 px-5 py-2.5 text-sm font-semibold text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-50">
            <Download className="h-4 w-4" />
            {busy ? "Preparing…" : "Download my data"}
          </button>
        </div>
      </main>
    </ScreenShell>
  );
}
